import React, { useState, useEffect } from 'react';
import { Input, Space, Typography, Spin } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const InlineEditField = ({ 
    value, 
    onSave, 
    placeholder = 'Click to edit',
    inputType = 'text',
    validateFn,
    formatDisplay,
    icon
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setEditValue(value || '');
    }, [value]);

    const handleEdit = () => {
        setIsEditing(true);
        setError('');
    };

    const handleCancel = () => {
        setEditValue(value || '');
        setIsEditing(false);
        setError('');
    };

    const handleSave = async () => {
        if (validateFn) {
            const validationError = validateFn(editValue);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        if (editValue === value) {
            setIsEditing(false);
            return;
        }

        setLoading(true);
        try {
            const success = await onSave(editValue);
            if (success) {
                setIsEditing(false);
                setError('');
            }
        } catch (err) {
            setError('Failed to save');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    const displayValue = formatDisplay ? formatDisplay(value) : value;

    if (isEditing) {
        return (
            <EditContainer>
                <Space>
                    <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        type={inputType}
                        autoFocus
                        disabled={loading}
                        status={error ? 'error' : ''}
                    />
                    <ActionIcon onClick={handleSave} disabled={loading}>
                        {loading ? <Spin size="small" /> : <CheckOutlined />}
                    </ActionIcon>
                    <ActionIcon onClick={handleCancel} disabled={loading}>
                        <CloseOutlined />
                    </ActionIcon>
                </Space>
                {error && <ErrorText>{error}</ErrorText>}
            </EditContainer>
        );
    }

    return (
        <DisplayContainer>
            <Space>
                {icon && icon}
                <Typography.Text>
                    {displayValue || placeholder}
                </Typography.Text>
                <EditIcon onClick={handleEdit}>
                    <EditOutlined />
                </EditIcon>
            </Space>
        </DisplayContainer>
    );
};

const EditContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DisplayContainer = styled.div`
    .ant-space-item:last-child {
        opacity: 0;
        transition: opacity 0.2s;
    }
    
    &:hover .ant-space-item:last-child {
        opacity: 1;
    }
`;

const EditIcon = styled.span`
    cursor: pointer;
    color: ${({ theme }) => theme.primary};
    font-size: 14px;
    
    &:hover {
        opacity: 0.8;
    }
`;

const ActionIcon = styled.span`
    cursor: pointer;
    color: ${({ theme }) => theme.primary};
    font-size: 16px;
    
    &:hover {
        opacity: 0.8;
    }
    
    ${({ disabled }) => disabled && `
        cursor: not-allowed;
        opacity: 0.5;
    `}
`;

const ErrorText = styled(Typography.Text)`
    color: #ff4d4f;
    font-size: 12px;
`;

export default InlineEditField;